/**
 * Time Formatting Utilities
 *
 * Modern relative and absolute time formatting for chat sessions
 */

/**
 * Converts a timestamp to milliseconds
 * Handles both number (milliseconds) and string (ISO format) timestamps
 */
const toMilliseconds = (timestamp: number | string): number => {
  if (typeof timestamp === 'number') {
    return timestamp;
  }
  return new Date(timestamp).getTime();
};

const months = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

/**
 * Formats a timestamp as a relative time string with smart granularity
 * Examples: "Just now", "2m ago", "3h ago", "Yesterday", "Nov 10"
 */
export const formatRelativeTime = (timestamp: number | string): string => {
  const now = Date.now();
  const timestampMs = toMilliseconds(timestamp);
  const diff = now - timestampMs;

  // Less than 1 minute
  if (diff < 60 * 1000) {
    return 'Just now';
  }

  // Less than 1 hour - show minutes
  if (diff < 60 * 60 * 1000) {
    const minutes = Math.floor(diff / (60 * 1000));
    return `${minutes}m ago`;
  }

  // Less than 24 hours - show hours
  if (diff < 24 * 60 * 60 * 1000) {
    const hours = Math.floor(diff / (60 * 60 * 1000));
    return `${hours}h ago`;
  }

  // Yesterday
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  yesterday.setHours(0, 0, 0, 0);

  const timestampDate = new Date(timestampMs);
  timestampDate.setHours(0, 0, 0, 0);

  if (timestampDate.getTime() === yesterday.getTime()) {
    return 'Yesterday';
  }

  // Less than 7 days - show day name
  if (diff < 7 * 24 * 60 * 60 * 1000) {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return days[new Date(timestampMs).getDay()] || 'Recent';
  }

  // Less than 1 year - show "Mon DD"
  const date = new Date(timestampMs);
  const currentYear = new Date().getFullYear();
  if (date.getFullYear() === currentYear) {
    return `${months[date.getMonth()]} ${date.getDate()}`;
  }

  // Older - show "Mon DD, YYYY"
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
};

/**
 * Formats a timestamp as an absolute time string for tooltips
 * Example: "Monday, November 10, 2025 at 3:42 PM"
 */
export const formatAbsoluteTime = (timestamp: number | string): string => {
  const date = new Date(toMilliseconds(timestamp));

  // Use Intl.DateTimeFormat for localized formatting
  const dateFormatter = new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const timeFormatter = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  return `${dateFormatter.format(date)} at ${timeFormatter.format(date)}`;
};

/**
 * Groups sessions by time periods for better organization
 * Returns: "Today", "Yesterday", "This Week", "Last Week", "This Month", "Last Month", "Older"
 */
export const getTimePeriod = (timestamp: number | string): string => {
  const now = Date.now();
  const timestampMs = toMilliseconds(timestamp);
  const diff = now - timestampMs;

  // Today
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const timestampDate = new Date(timestampMs);
  timestampDate.setHours(0, 0, 0, 0);

  if (timestampDate.getTime() >= today.getTime()) {
    return 'Today';
  }

  // Yesterday
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  yesterday.setHours(0, 0, 0, 0);

  if (timestampDate.getTime() === yesterday.getTime()) {
    return 'Yesterday';
  }

  // This week (last 7 days)
  if (diff < 7 * 24 * 60 * 60 * 1000) {
    return 'This Week';
  }

  // Last week (7-14 days ago)
  if (diff < 14 * 24 * 60 * 60 * 1000) {
    return 'Last Week';
  }

  // This Month - check if in current calendar month
  const nowDate = new Date(now);
  const tsDate = new Date(timestampMs);

  if (tsDate.getFullYear() === nowDate.getFullYear() && tsDate.getMonth() === nowDate.getMonth()) {
    return 'This Month';
  }

  // Last Month - check if in previous calendar month
  const lastMonthDate = new Date(nowDate);
  lastMonthDate.setMonth(lastMonthDate.getMonth() - 1);

  if (
    tsDate.getFullYear() === lastMonthDate.getFullYear() &&
    tsDate.getMonth() === lastMonthDate.getMonth()
  ) {
    return 'Last Month';
  }

  // Older
  return 'Older';
};

/**
 * Formats time for message timestamps within chat
 * Example: "3:42 PM"
 */
export const formatMessageTime = (timestamp: number | string): string => {
  const date = new Date(toMilliseconds(timestamp));

  const timeFormatter = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  return timeFormatter.format(date);
};
